import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/apiClient.ts';
import { RefreshCw, Calendar, DollarSign, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DateInput from '../common/DateInput';

/**
 * Balances.jsx — صفحة "أرصدة المجموعات"
 * - تحميل أولي بدون تاريخ
 * - فلترة بتاريخ من/إلى بصيغة YYYY-MM-DD
 * - جدول بخمسة أعمدة: (اسم المجموعة، سعر المجموعة، واصل نقداً، الأقساط المستلمة، الرصيد المتبقي)
 * - فوتر يحوي المجاميع
 * - يحافظ على الثيم العام (Tailwind + dark mode)
 */

const API_URL = '/v1/reports/group-balances';
// داخل apiClient.ts قبل export default api;
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // 👇 أضف هذا السطر
  const tenant = localStorage.getItem("tenant") || "default-tenant-1";
  config.headers["X-Tenant"] = tenant;

  return config;
});


const Balances = () => {
  const { t } = useTranslation?.() ?? { t: (k) => k };

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // محاولة ذكية: جرّب GET أولاً، وإذا الـ API يطلب POST فقط، نحاول POST تلقائيًا.
  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (from) params.df = from;
      if (to) params.dt = to;

      let resp;
      try {
        resp = await api.get(API_URL, { params });
      } catch (e) {
        // إذا فشل الـ GET (مثلاً 405)، جرّب POST
        resp = await api.post(API_URL, { df: from || null, dt: to || null });
      }
      const data = resp?.data?.data ?? resp?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Network Error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // تحميل أولي بدون تاريخ
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // فلترة حسب البحث
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    return rows.filter(r =>
      (r.group_name || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [rows, search]);

  const totals = useMemo(() => {
    const agg = { total_price: 0, cash_received: 0, installments_paid: 0, group_expenses: 0, balance: 0 };
    for (const r of filteredRows) {
      agg.total_price       += Number(r?.total_price ?? 0);
      agg.cash_received     += Number(r?.cash_received ?? 0);
      agg.installments_paid += Number(r?.installments_paid ?? 0);
      agg.group_expenses    += Number(r?.group_expenses ?? 0);
      agg.balance           += Number(r?.balance ?? 0);
    }
    return agg;
  }, [filteredRows]);

  return (
    <div className="text-gray-900 dark:text-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="w-full mx-auto">
        {/* Header Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-reverse space-x-3">
                <div className="bg-white/20 rounded-full p-3">
                  <DollarSign className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{t('أرصدة المجموعات') || 'أرصدة المجموعات'}</h2>
                  <p className="text-blue-100 mt-1">{t('عرض أرصدة وتفاصيل المجموعات') || 'عرض أرصدة وتفاصيل المجموعات'}</p>
                </div>
              </div>
              {/* Search داخل الهيدر */}
              <div className="relative w-64 sm:w-80">
                <Search className="absolute left-3 rtl:right-3 rtl:left-auto top-1/2 transform -translate-y-1/2 text-white/80 h-5 w-5" />
                <input
                  type="text"
                  placeholder={t('البحث في المجموعات...') || 'البحث في المجموعات...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 rtl:pr-10 rtl:pl-4 pr-4 py-2.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200"
                />
              </div>
            </div>
          </div>

          {/* فلاتر التاريخ */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
            <div className="flex flex-wrap items-end gap-4">
              {/* من */}
              <div className="flex-1 min-w-[150px] max-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('من تاريخ') || 'مِن تاريخ'}
                </label>
                <div className="relative">
                  <DateInput

 value={from}
 onChange={(e) => setFrom(e.target.value)}
 className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
 />
                </div>
              </div>

              {/* إلى */}
              <div className="flex-1 min-w-[150px] max-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('الى تاريخ') || 'إلى تاريخ'}
                </label>
                <div className="relative">
                  <DateInput

 value={to}
 onChange={(e) => setTo(e.target.value)}
 className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
 />
                </div>
              </div>

              {/* زر تحديث */}
              <button
                onClick={fetchData}
                disabled={loading}
                className="inline-flex items-center justify-center px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium shadow-sm transition-colors duration-200"
                title={t('تحديث') || 'تحديث'}
              >
                <RefreshCw className={`w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? (t('يتم الجلب...') || 'يتم الجلب...') : (t('تحديث') || 'تحديث')}
              </button>
            </div>

            {error && (
              <div className="mt-4 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}
          </div>

          {/* Table */}
          <div className="p-4 sm:p-6">
            <div className="app-table-wrap">
            <table className="app-table min-w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-600">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {t('اسم المجموعة') || 'اسم المجموعة'}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {t('سعر المجموعة') || 'سعر المجموعة'}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {t('واصل نقداً') || 'واصل نقداً'}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {t('الأقساط المستلمة') || 'الأقساط المستلمة'}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {t('مصاريف المجموعة') || 'مصاريف المجموعة'}
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {t('الرصيد المتبقي') || 'الرصيد المتبقي'}
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center text-gray-500 dark:text-gray-400">
                      {t('يتم الجلب...') || 'يتم الجلب...'}
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center text-gray-500 dark:text-gray-400">
                      {t('لا توجد بيانات') || 'لا توجد بيانات'}
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((r, idx) => (
                    <tr
                      key={r.gr_id}
                      className={`border-b border-gray-100 dark:border-gray-700/50 transition-colors duration-150 hover:bg-blue-50/50 dark:hover:bg-gray-700/30 ${
                        idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-700/40' : ''
                      }`}
                    >
                      <td className="px-6 py-5 whitespace-nowrap">
                        <span className="text-base font-medium text-gray-900 dark:text-gray-100">
                          {r.group_name}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-base text-gray-900 dark:text-gray-100 text-right font-semibold whitespace-nowrap">
                        {Number(r.total_price || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                      </td>
                      <td className="px-6 py-5 text-base text-emerald-700 dark:text-emerald-400 text-right font-semibold whitespace-nowrap">
                        {Number(r.cash_received || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                      </td>
                      <td className="px-6 py-5 text-base text-emerald-700 dark:text-emerald-400 text-right font-semibold whitespace-nowrap">
                        {Number(r.installments_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                      </td>
                      <td className="px-6 py-5 text-base text-red-600 dark:text-red-400 text-right font-semibold whitespace-nowrap">
                        {Number(r.group_expenses || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                      </td>
                      <td className={`px-6 py-5 text-base text-right font-bold whitespace-nowrap ${
                        Number(r.balance || 0) > 0
                          ? 'text-orange-700 dark:text-orange-400'
                          : Number(r.balance || 0) === 0
                          ? 'text-gray-600 dark:text-gray-300'
                          : 'text-red-700 dark:text-red-400'
                      }`}>
                        {Number(r.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                      </td>
                    </tr>
                  ))
                )}
              </tbody>

              {/* فوتر المجاميع */}
              {filteredRows.length > 0 && (
                <tfoot className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
                  <tr className="border-t-2 border-blue-200 dark:border-gray-600">
                    <td className="px-6 py-4 text-left text-sm font-bold text-blue-700 dark:text-blue-300">
                      {t('إجمالي') || 'المجاميع'}
                    </td>
                    <td className="px-6 py-4 text-right text-base font-extrabold text-gray-900 dark:text-gray-100">
                      {totals.total_price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                    <td className="px-6 py-4 text-right text-base font-extrabold text-emerald-700 dark:text-emerald-400">
                      {totals.cash_received.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                    <td className="px-6 py-4 text-right text-base font-extrabold text-emerald-700 dark:text-emerald-400">
                      {totals.installments_paid.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                    <td className="px-6 py-4 text-right text-base font-extrabold text-red-600 dark:text-red-400">
                      {totals.group_expenses.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                    <td className={`px-6 py-4 text-right text-base font-extrabold ${
                      totals.balance > 0
                        ? 'text-orange-700 dark:text-orange-400'
                        : totals.balance === 0
                        ? 'text-gray-600 dark:text-gray-300'
                        : 'text-red-700 dark:text-red-400'
                    }`}>
                      {totals.balance.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Balances;
